import { ForwardedRef, forwardRef, useEffect, useRef } from 'react';
import { Box } from '@gemini/core';
import { IFieldProps } from '@mfe-types/field';
import useNavigation from '@mfe-presentation/hooks/useNavigation';
import { useFormContext } from 'react-hook-form';
import { StateMessage } from '@gemini/ui';

export const SMS_CODE_LENGTH = 6;
const KEYBOARD_KEYS = ['Backspace', 'ArrowLeft', 'ArrowRight', 'Delete'];
const NOT_NUMBER_REGEX = /[^0-9]/;

const isNotNumber = (value: any) => NOT_NUMBER_REGEX.test(value);

const PhoneNumberVerificationCode = forwardRef(
    ({ field, texts }: IFieldProps<any, any>, ref: ForwardedRef<HTMLInputElement>) => {
        const inputs = useRef<HTMLInputElement[] | null>([]);
        const { handleNavigation } = useNavigation();
        const { getValues } = useFormContext();

        useEffect(() => {
            const handleOnPaste = (e: ClipboardEvent) => handlePaste(e);
            inputs.current?.forEach((input) => {
                input.addEventListener('paste', handleOnPaste);
            });
            return () => {
                inputs.current?.forEach((input) => {
                    input.removeEventListener('paste', handleOnPaste);
                });
            };
        }, []);

        const handleChange = (e: React.FormEvent<HTMLInputElement>, slotIndex: number) => {
            const value = e.currentTarget.value;
            if (NOT_NUMBER_REGEX.test(value)) return;

            const nextSlotIndex = slotIndex + 1;
            const newCode = field.value ? field.value.split('') : [''];
            newCode[slotIndex] = value;

            if (inputs.current && slotIndex !== SMS_CODE_LENGTH - 1 && value) {
                inputs.current[nextSlotIndex].focus();
            }

            field.onChange(newCode.join(''));
        };

        const handleKeyDown = (e: React.KeyboardEvent, slotIndex: number) => {
            if (!inputs.current) return;

            const currentCode = field.value ? field.value.split('') : [''];
            const key = e.code;
            const previousSlot = inputs.current[slotIndex - 1];
            const nextSlot = inputs.current[slotIndex + 1];
            const isLastSlot = slotIndex === SMS_CODE_LENGTH - 1;
            const isFirstSlot = slotIndex === 0;

            const isDeletionKey = ['Backspace', 'Delete'].includes(key);

            if (KEYBOARD_KEYS.includes(key)) {
                if (
                    ((isDeletionKey && !currentCode[slotIndex]) || key === 'ArrowLeft') &&
                    previousSlot &&
                    !isFirstSlot
                ) {
                    e.preventDefault();
                    previousSlot.focus();
                    previousSlot.select();
                }

                if (key === 'ArrowRight' && nextSlot && !isLastSlot) {
                    e.preventDefault();
                    nextSlot.focus();
                    nextSlot.select();
                }
            } else if (key === 'Enter') {
                e.preventDefault();
                handleNavigation('next', getValues());
            }
        };

        const handlePaste = (e: ClipboardEvent) => {
            e.preventDefault();

            if (!e.clipboardData || !inputs.current) return;

            const clipBoardData = e.clipboardData.getData('text');

            if (clipBoardData.length > SMS_CODE_LENGTH) {
                // larger than required
                return;
            }

            const clipBoardDataToArray = new Array(SMS_CODE_LENGTH)
                .fill('')
                .map((_, index) => clipBoardData[index] ?? '');

            if (clipBoardDataToArray.some(isNotNumber)) return; // contains alphabetic characters

            inputs.current[clipBoardData.length - 1].focus();
            field.onChange(clipBoardDataToArray.join(''));
        };

        return (
            <>
                <Box id={`wlef-${texts.label}`} typography="typography.body.16.bold">
                    {texts.label}
                </Box>
                <Box
                    display="grid"
                    gridTemplateColumns="repeat(6, 1fr)"
                    gridTemplateRows="1fr"
                    width="100%"
                    marginVertical="spacing.8"
                    gap={{
                        'breakpoint.base': 'spacing.4',
                        'breakpoint.xs': 'spacing.8',
                    }}
                >
                    {Array.from({ length: SMS_CODE_LENGTH }).map((_, index) => {
                        const slotValue = field.value?.[index] ? field.value[index] : '';
                        return (
                            <Box
                                testId={`phone-number-verification-code-${index}`}
                                as="input"
                                name={field.name}
                                type={field.type}
                                inputMode={field.inputMode}
                                aria-required={!field.isOptional}
                                aria-labelledby={`wlef-${texts.label}`}
                                key={index}
                                textAlign="center"
                                width="44px"
                                minWidth="0"
                                paddingHorizontal="spacing.16"
                                paddingVertical="spacing.12"
                                borderRadius="radius.8"
                                borderColor={
                                    field.error
                                        ? 'color.border.status.error.default'
                                        : 'color.border.subdued.default'
                                }
                                maxLength={1}
                                value={slotValue}
                                onChange={(e: React.KeyboardEvent<HTMLInputElement>) =>
                                    handleChange(e, index)
                                }
                                {...{ autoFocus: index === 0 }}
                                onKeyDown={(e: React.KeyboardEvent<HTMLInputElement>) =>
                                    handleKeyDown(e, index)
                                }
                                outlineColor={
                                    field.error
                                        ? 'color.border.status.error.default'
                                        : 'color.border.interactive.default'
                                }
                                ref={(ref: any) =>
                                    inputs.current && ref && inputs.current.push(ref)
                                }
                            />
                        );
                    })}
                </Box>
                {field.error && <StateMessage variant="error">{field.error}</StateMessage>}
            </>
        );
    },
);

export default PhoneNumberVerificationCode;
